import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { type Schema as PMSchema } from '@atlaskit/editor-prosemirror/model';
import { ACTION } from '@atlaskit/editor-common/analytics';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import { expVal } from '@confluence/feature-experiments';

import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType';
import type { StepsDatabase } from '../stepsDatabase';
import { fireOfflineStorageAnalyticsEvent, parseErrorMessage } from '../analytics';

import { applyStepsCommand, noStepsCommand, retryCommand, tooManyRetriesCommand } from './commands';

const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;

function handleRetry(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	retrySource: string,
	error?: unknown,
) {
	const sharedPluginState = api?.editorOfflineStorage?.sharedState.currentState();
	const retryCount = sharedPluginState?.retryCount ?? 0;
	if (retryCount >= MAX_RETRIES) {
		api?.core.actions.execute(tooManyRetriesCommand());

		fireOfflineStorageAnalyticsEvent({
			api,
			action: ACTION.OFFLINE_STORAGE_TOO_MANY_RETRIES_ERROR,
			errorMessage: `Exceeded max retries (${MAX_RETRIES}) with delay of ${RETRY_DELAY}ms via ${retrySource}`,
			errorInfo: parseErrorMessage(error),
			retrySource,
			maxRetries: MAX_RETRIES,
		});
	} else {
		setTimeout(() => {
			api?.core.actions.execute(retryCommand(retryCount));
		}, RETRY_DELAY);
	}
}

export function restoreStepsAction(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
) {
	return async (pmSchema: PMSchema) => {
		try {
			const databases = await window.indexedDB.databases();
			const dbExists = Boolean(databases.find((db) => db.name === stepsDatabase.dbName));

			if (!dbExists) {
				api?.core.actions.execute(noStepsCommand());
				return;
			}

			if (expVal('platform_editor_offline_editing_idx_db_validation', 'isEnabled', false)) {
				// If there's an error, the validateIndexedDBAccess will disable the plugin.
				const isIndexedDBAvailable = await stepsDatabase.validateIndexedDBAccess();
				if (!isIndexedDBAvailable) {
					return;
				}
			}

			const { steps } = await stepsDatabase.getData(pmSchema);
			const dispatched = api?.core.actions.execute(applyStepsCommand(steps, api));
			if (!dispatched) {
				handleRetry(api, 'stepsNotDispatched');
				return;
			}

			try {
				await stepsDatabase.clear();
			} catch (e) {
				// steps were applied but failed to clear, continue
			}
		} catch (error) {
			handleRetry(api, 'restoreStepsError', error);
		}
	};
}
